<template>
  <div class="product-view">
    <MarxentNotification />
    <Alerts
      :product-id="productId"
      :alert-props="alertProps"
    />
    <component
      :is="ProductTypeComponent"
      :class="{ 'spacing-expriment': isSpacingExperimentEnabled }"
    />
    <component
      :is="ModalTypeComponent"
      page-type="pdp"
      @error-alert="onErrorAlert"
    />
  </div>
</template>

<script setup>
import {
  computed, provide, defineAsyncComponent,
} from 'vue';

import StandardProduct from '@components/ProductCard/StandardProduct';
import GiftCardProduct from '@components/ProductCard/GiftCardProduct';
import GiftWithPurchase from '@components/ProductCard/GiftWithPurchase';
import MasterProduct from '@components/ProductCard/MasterProduct';
import ConsolidatedProduct from '@components/ProductCard/ConsolidatedProduct';
import UnavailableProduct from '@components/ProductCard/UnavailableProduct';
import ConsolidatedLatchKeyProduct from '@components/ProductCard/ConsolidatedLatchKeyProduct';
import ConsolidatedStandard from '@components/ProductCard/ConsolidatedStandard';
import MarxentNotification from '@components/ProductCard/MarxentNotification';

import { useAlertsProps, Alerts } from '@modules/alerts';
import { useProduct } from '@modules/product';
import { useModal } from '@composables/utils/useModal';
import { useProductType } from '@composables/useProductType';
import { useExperiments } from '@composables/useExperiments';

import { UNLOCK_SCROLL } from './constants';
import { unlockScroll } from './utils';

const HexaModal = defineAsyncComponent(() => import(/* webpackChunkName: 'HexaModal' */ '@components/Modals/HexaModal'));
const MarxentModal = defineAsyncComponent(() => import(/* webpackChunkName: 'MarxentModal' */ '@components/Modals/MarxentModal'));
const VTOModal = defineAsyncComponent(() => import(/* webpackChunkName: 'VTOModal' */ '@common-vue/virtual-try-on-experience/src/components/VirtualTryOnExperienceModal'));
const MainImageOverlayModal = defineAsyncComponent(() => import(/* webpackChunkName: 'MainImageOverlay' */ '@components/common/Media/ImageOverlay/MainImageOverlay'));

const { productId } = useProduct();
const { type } = useProductType({ productId });
const { activeModal } = useModal();
const {
  experimentFlags: { isSpacingExperimentEnabled },
} = useExperiments();

/** @type {Record<ModalName, import('vue').DefineComponent<any, any, any>>} */
const ModalTypeComponents = {
  hexa: HexaModal,
  marxent: MarxentModal,
  VTO: VTOModal,
  'main-image-overlay': MainImageOverlayModal,
};

const ModalTypeComponent = computed(() => ModalTypeComponents[activeModal.value]);

/** @type {Record<ProductType, import('vue').DefineComponent<any, any, any>>} */
const ProductTypeComponents = {
  giftCard: GiftCardProduct,
  master: MasterProduct,
  giftWithPurchase: GiftWithPurchase,
  unavailable: UnavailableProduct,
  latchKey: ConsolidatedLatchKeyProduct,
  consolidatedStandard: ConsolidatedStandard,
  consolidatedMaster: ConsolidatedProduct,
  consolidatedLatchKey: ConsolidatedLatchKeyProduct,
  standard: StandardProduct,
};

const ProductTypeComponent = computed(() => ProductTypeComponents[type]);

const { onErrorAlert, alertProps } = useAlertsProps();

// Provide unlockScroll function for external dependencies that
// don't trigger 'close' via useModal inside of pdp
provide(UNLOCK_SCROLL, unlockScroll);

</script>

<style lang="scss" scoped>
.product-view {
  flex: 1;
  max-width: 100%;
}
</style>
